/**
 * FormLibrary: Form utilities for reading, creating, and interacting with
 * HTML forms. Extracted from Shared/utils.js (MooTools-free).
 */

interface GetValuesOptions {
   prefix?: string;
   submitDisabled?: boolean;
   ignoreClass?: string | null;
}

interface FieldDefinition {
   type: 'text' | 'switch';
   label?: string;
   value?: string | boolean;
   properties?: Record<string, string>;
}

interface FormDefinition {
   id?: string;
   class?: string;
   fields?: Record<string, FieldDefinition>;
   submit?: string;
}

function resolveForm(form: string | HTMLFormElement): HTMLFormElement | null {
   const el = typeof form === 'string' ? document.getElementById(form) : form;
   if (!el || el.tagName.toLowerCase() !== 'form') {
      return null;
   }
   return el as HTMLFormElement;
}

export const FormLibrary = {
   getFields(form: HTMLElement): HTMLElement[] {
      return Array.from(
         form.querySelectorAll<HTMLElement>('input,select,textarea,div[contenteditable]')
      );
   },

   getEditableFields(form: HTMLElement): HTMLElement[] {
      return this.getFields(form).filter(
         el => (el as HTMLInputElement).type !== 'hidden' && !(el as HTMLInputElement).disabled
      );
   },

   getValues(
      form: string | HTMLFormElement,
      options?: GetValuesOptions
   ): Record<string, any> | undefined {
      const formEl = resolveForm(form);
      if (!formEl) {
         return;
      }

      const indexRegex = /\[([\w#-]*)]/;
      const obj: Record<string, any> = {};

      const opts = {
         prefix: '',
         submitDisabled: false,
         ignoreClass: null as string | null,
         ...options,
      };

      Array.from(formEl.elements).forEach(el => {
         const input = el as HTMLInputElement;
         if (!input.name) {
            return;
         }
         if (input.name.slice(0, Math.max(0, opts.prefix.length)) !== opts.prefix) {
            return;
         }
         if (
            input.type &&
            (input.type === 'radio' || input.type === 'checkbox') &&
            !input.checked
         ) {
            return;
         }
         if (input.disabled && !opts.submitDisabled) {
            return;
         }

         let name = input.name;
         const indices: string[] = [];
         let groups: RegExpMatchArray | null = null;
         while ((groups = name.match(indexRegex))) {
            indices.push(groups[1]);
            name = name.replace(indexRegex, '');
         }
         indices.unshift(name);

         let pointer: any = obj;
         let index: string;
         let i: number;
         for (i = 0; i < indices.length - 1; i++) {
            let next: any;
            index = indices[i];
            if (index === '') {
               next = indices[i + 1] === '' ? [] : {};
               pointer.push(next);
               pointer = next;
            } else {
               if (!pointer[index]) {
                  next = indices[i + 1] === '' ? [] : {};
                  pointer[index] = next;
               }
               pointer = pointer[index];
            }
         }

         const value =
            opts.ignoreClass && el.classList.contains(opts.ignoreClass)
               ? ''
               : (el as HTMLInputElement).value;

         index = indices[i];
         if (index === '') {
            pointer.push(value);
         } else {
            pointer[index] = value;
         }
      });

      return obj;
   },

   getFirstField(form: HTMLElement): HTMLElement | undefined {
      const els = this.getEditableFields(form);

      const withIndices = els.filter(el => el.tabIndex >= 1);

      const firstByIndex = withIndices.sort((a, b) => a.tabIndex - b.tabIndex)[0];

      return firstByIndex || els[0];
   },

   focusFirst(form: HTMLElement): void {
      const first = this.getFirstField(form);
      if (first) {
         first.focus();
      }
   },

   selectedValue(el: string | HTMLSelectElement): string {
      const selectEl =
         typeof el === 'string' ? (document.getElementById(el) as HTMLSelectElement) : el;
      return selectEl.options[selectEl.selectedIndex].value;
   },

   submit(form: string | HTMLFormElement): boolean | void {
      const formEl = resolveForm(form);
      if (!formEl) {
         return;
      }

      if (typeof formEl.onsubmit === 'function' && !formEl.onsubmit(new SubmitEvent('submit'))) {
         return false;
      } else {
         formEl.submit();
      }
   },

   create(obj: FormDefinition): HTMLFormElement {
      const form = document.createElement('form');
      if (obj.id) {
         form.id = obj.id;
      }
      if (obj['class']) {
         form.className = obj['class'];
      }

      const formBody = document.createElement('div');
      formBody.className = 'formBody';
      form.append(formBody);

      for (const [name, field] of Object.entries(obj.fields || {})) {
         const box = document.createElement('div');
         box.className = 'formField';
         formBody.append(box);

         const id = field.properties ? field.properties.id : '';

         if (field.label) {
            const label = document.createElement('label');
            label.setAttribute('for', id || name);
            label.textContent = field.label;
            box.append(label);
         }

         let el: HTMLElement | null = null;

         if (field.type === 'text') {
            const input = document.createElement('input');
            input.name = name;
            input.type = 'text';
            input.value = (field.value as string) || '';
            for (const [key, val] of Object.entries(field.properties || {})) {
               input.setAttribute(key, val);
            }
            el = input;
         }

         if (field.type === 'switch') {
            const input = document.createElement('input');
            input.name = name;
            input.type = 'checkbox';
            input.value = String(field.value || false);
            input.className = 'switch-input';
            for (const [key, val] of Object.entries(field.properties || {})) {
               input.setAttribute(key, val);
            }

            const span = document.createElement('span');
            span.className = 'switch-track';

            const label = document.createElement('label');
            label.className = 'switch';
            label.append(input);
            label.append(span);
            el = label;
         }

         if (el) {
            box.append(el);
         }
      }

      const submitBox = document.createElement('div');
      submitBox.className = 'formField';
      const button = document.createElement('button');
      button.type = 'submit';
      button.className = 'button button-action-solid';
      button.textContent = obj.submit || 'Submit';
      submitBox.append(button);
      formBody.append(submitBox);

      return form;
   },
};
